import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Spinner } from "../../../components/ui/spinner";
import AccessLevelTabs from "./AccessLevelTabs";
import FormField from "./FormField";
import ProjectSelect from "./ProjectSelect";
import UserMultiSelect from "./UserMultiSelect";
import { TOOL_ACCESS_LEVELS } from "../constants";
import { normalizeSharingUsers } from "../adapters";
import { useUpdateToolSharing } from "../hooks/useUpdateToolSharing";
import type {
  MCPConnectionTool,
  MCPSharingUserItem,
  MCPToolSharing,
} from "../../../services/agent-tools-service";
import type { ToolAccessLevel } from "../types";
import { useTranslation } from "react-i18next";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  tool: MCPConnectionTool | null;
};

// Map the tool's stored sharing scope onto the 3 tabs (organization collapses
// into "inherit" since tools only expose inherit/project/user).
const toAccessLevel = (
  sharing: MCPToolSharing | undefined,
): ToolAccessLevel => {
  if (sharing?.scope === "project") return "project";
  if (sharing?.scope === "user") return "user";
  return "inherit";
};

const ToolAccessDialog = ({
  open,
  onOpenChange,
  connectionId,
  tool,
}: Props) => {
  const { t } = useTranslation();
  const [level, setLevel] = useState<ToolAccessLevel>("inherit");
  const [projectId, setProjectId] = useState("");
  const [users, setUsers] = useState<MCPSharingUserItem[]>([]);

  const mutation = useUpdateToolSharing({
    onSuccess: () => onOpenChange(false),
  });

  // Seed from the tool's current sharing whenever the dialog opens.
  useEffect(() => {
    if (!open || !tool) return;
    const sharing = tool.sharing;
    setLevel(toAccessLevel(sharing));
    setProjectId(sharing?.scope === "project" ? sharing.item : "");
    setUsers(
      sharing?.scope === "user" ? normalizeSharingUsers(sharing.items) : [],
    );
  }, [open, tool]);

  const isValid =
    level === "inherit" ||
    (level === "project" && Boolean(projectId)) ||
    (level === "user" && users.length > 0);

  const handleChangeLevel = (next: ToolAccessLevel) => {
    setLevel(next);
    if (next !== "project") setProjectId("");
    if (next !== "user") setUsers([]);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!tool || !isValid || mutation.isPending) return;

    const sharing: MCPToolSharing =
      level === "project"
        ? { scope: "project", item: projectId }
        : level === "user"
          ? { scope: "user", items: users }
          : { scope: "inherit" };

    mutation.mutate({ connectionId, toolId: tool.id, sharing });
  };

  const handleOpenChange = (next: boolean) => {
    if (mutation.isPending && !next) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="gap-0 sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t("mcpPage.toolAccess.title", { name: tool?.name })}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-5">
          <div
            className={`grid gap-4 ${
              level === "inherit" ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"
            }`}
          >
            <FormField label={t("mcpPage.fields.accessLevel")}>
              <AccessLevelTabs
                value={level}
                onChange={handleChangeLevel}
                options={TOOL_ACCESS_LEVELS}
              />
            </FormField>

            {level === "project" && (
              <FormField label={t("mcpPage.fields.project")}>
                <ProjectSelect value={projectId} onChange={setProjectId} />
              </FormField>
            )}

            {level === "user" && (
              <FormField label={t("mcpPage.fields.users")}>
                <UserMultiSelect value={users} onChange={setUsers} />
              </FormField>
            )}
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              disabled={mutation.isPending}
              className="h-10 rounded-[8px] border border-border bg-card px-5 text-sm font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
            >
              {t("mcpPage.actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={!isValid || mutation.isPending}
              aria-busy={mutation.isPending}
              className="flex h-10 items-center gap-2 rounded-[8px] bg-primary px-5 text-sm font-semibold text-primary-foreground transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {mutation.isPending && <Spinner className="size-4" />}
              {mutation.isPending
                ? t("mcpPage.actions.saving")
                : t("mcpPage.actions.change")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ToolAccessDialog;
